import { FC, useState } from 'react';
import {
  VStack,
  HStack,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Icon,
  Box,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Textarea
} from '@chakra-ui/react';
import { MdArrowBack, MdSave } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { useForm } from 'react-hook-form';
import { Panel, PageHeading } from '@bemap/ui';
import { createContent, Content } from '@/services/university-service';

interface QuizContentFormData {
  title: string;
  description: string;
  passingScore: number;
  timeLimit: number;
  order: number;
  quizType: 'multiple_choice' | 'true_false' | 'essay';
}

const CreateQuizContentPage: FC = () => {
  const navigate = useNavigate();
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const toast = useToast();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<QuizContentFormData>({
    defaultValues: {
      title: '',
      description: '',
      passingScore: 70,
      timeLimit: 30,
      order: 1,
      quizType: 'multiple_choice'
    }
  });

  const handleBack = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
  };

  const onSubmit = async (data: QuizContentFormData) => {
    setIsSubmitting(true);

    try {
      const contentData: Partial<Content> = {
        title: data.title,
        data: {
          title: data.title,
          description: data.description,
          passingScore: data.passingScore,
          allowRetake: false,
          showCorrectAnswers: false,
          questions: []
        },
        type: 'quiz',
        order: data.order,
        moduleId: parseInt(moduleId!),
        completed: false
      };

      const response = await createContent(contentData);

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast({
          title: t('university.content.quiz_created'),
          description: `${t('university.content.create_quiz')}"${data.title}" ${t('university.content.quiz_created')}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Navigate back to the course
        navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
      } else {
        throw new Error(response.message || t('university.content.error_creating_quiz'));
      }
    } catch (error: any) {
      console.error('Error creating quiz content:', error);
      toast({
        title: t('university.content.error_creating_quiz'),
        description: error.message || t('general.error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={t('university.content.create_quiz')} actions={[
        {
          label: t('university.content.back_to_content_types'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack align="stretch" spacing={6}>
          {/* Quiz Content Form */}
          <Panel heading={t('university.content.quiz_information')} padding={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.title} isRequired>
                <FormLabel>{t('university.content.title')}</FormLabel>
                <Input
                  {...register('title', {
                    required: t('university.validation.title_required'),
                    minLength: { value: 3, message: t('university.validation.title_min_length') }
                  })}
                  placeholder={t('university.placeholders.quiz_title')}
                />
                <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>{t('university.content.description')}</FormLabel>
                <Textarea
                  {...register('description')}
                  placeholder={t('university.placeholders.quiz_description')}
                  rows={3}
                />
              </FormControl>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('university.content.quiz_type')}</FormLabel>
                  <Select {...register('quizType')}>
                    <option value="multiple_choice">{t('university.content.quiz_types.multiple_choice')}</option>
                    <option value="true_false">{t('university.content.quiz_types.true_false')}</option>
                    <option value="essay">{t('university.content.quiz_types.essay')}</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.content.order')}</FormLabel>
                  <NumberInput min={1} defaultValue={1}>
                    <NumberInputField {...register('order', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>

              <HStack spacing={4}>
                <FormControl>
                  <FormLabel>{t('university.content.passing_score')}</FormLabel>
                  <NumberInput min={0} max={10} defaultValue={70}>
                    <NumberInputField {...register('passingScore', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {t('university.content.percentage')}
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>{t('university.content.time_limit')}</FormLabel>
                  <NumberInput min={0} defaultValue={30}>
                    <NumberInputField {...register('timeLimit', { valueAsNumber: true })} />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    {t('university.content.minutes')}
                  </Text>
                </FormControl>
              </HStack>
            </VStack>
          </Panel>

          {/* Quiz Configuration Info */}
          <Panel heading={t('university.content.quiz_configuration')} padding={6}>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {t('university.content.quiz_setup_note')}
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {t('university.content.quiz_setup_description')}
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>
                  {t('university.content.supported_question_types')}:
                </Text>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.600">• {t('university.content.question_types.multiple_choice')}</Text>
                  <Text fontSize="sm" color="gray.600">• {t('university.content.question_types.true_false')}</Text>
                  <Text fontSize="sm" color="gray.600">• {t('university.content.question_types.essay')}</Text>
                  <Text fontSize="sm" color="gray.600">• {t('university.content.question_types.short_answer')}</Text>
                </VStack>
              </Box>
            </VStack>
          </Panel>

          {/* Action Buttons */}
          <HStack justify="space-between">
            <Button
              variant="outline"
              onClick={handleBack}
              isDisabled={isSubmitting}
            >
              {t('actions.cancel')}
            </Button>

            <HStack spacing={3}>
              <Button
                type="submit"
                colorScheme="blue"
                leftIcon={<Icon as={MdSave} />}
                isLoading={isSubmitting}
                loadingText={t('general.creating')}
                isDisabled={!isValid}
              >
                {t('university.content.create_quiz')}
              </Button>
            </HStack>
          </HStack>
        </VStack>
      </form>
    </VStack>
  );
};

export default CreateQuizContentPage; 